/**
 * Step IDD adeguatezza e AML verifica — FASE 4.2.11.10 (questionario vita).
 *
 * Vincoli normativi:
 *  - IDD (Reg. UE 2016/97 art. 30 + Reg. IVASS 41/2018 art. 58)
 *  - AML (Reg. IVASS 44/2019 + D.lgs. 231/2007)
 *
 * Output:
 *  - IddVitaStep raccoglie 9 domande adeguatezza e POSTa a /api/compliance/idd-vita
 *  - AmlVerificaStep raccoglie 8 domande KYC e POSTa a /api/compliance/aml-verifica
 *  Entrambi mostrano feedback non-bloccante (warning soft).
 */
import { useState } from "react";
import axios from "axios";
import { Shield, AlertTriangle, Info, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ----------------------- IDD adeguatezza vita -----------------------
const OBIETTIVI_OPZIONI = [
  { id: "protezione_premorienza", label: "Protezione famiglia da rischio premorienza" },
  { id: "pensione_integrativa", label: "Risparmio per pensione integrativa" },
  { id: "accumulo_capitale", label: "Accumulo capitale (casa, figli, studi)" },
  { id: "protezione_patrimonio", label: "Protezione patrimonio" },
  { id: "trasmissione_ereditaria", label: "Trasmissione ereditaria" },
];

function RadioGroup({ name, value, options, onChange, "data-testid": testid }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid={testid}>
      {options.map(o => (
        <label key={o.id} className={`text-xs px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
          value === o.id ? "bg-emerald-50 border-emerald-300 text-emerald-900 font-semibold" : "bg-white border-zinc-200 text-zinc-700 hover:border-emerald-200"
        }`}>
          <input type="radio" name={name} value={o.id} checked={value === o.id}
            onChange={() => onChange(o.id)} className="sr-only" />
          {o.label}
        </label>
      ))}
    </div>
  );
}

export function IddVitaStep({ formData, update, richiestaId }) {
  const [esito, setEsito] = useState(null);
  const [score, setScore] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const v = formData.idd || {};

  const updateIdd = (patch) => update({ idd: { ...v, ...patch } });
  const toggleObiettivo = (id) => {
    const cur = new Set(v.obiettivi || []);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    updateIdd({ obiettivi: Array.from(cur) });
  };

  const isComplete = (v.obiettivi?.length || 0) > 0 && v.orizzonte && v.propensione_rischio && v.perdita_max && v.esperienza_finanziaria && v.situazione_reddituale && v.nucleo_familiare && v.patrimonio_finanziario && v.capacita_risparmio;

  const submit = async () => {
    if (!isComplete) { toast.error("Compila tutte le domande della profilatura"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/compliance/idd-vita`, { ...v, richiesta_id: richiestaId || null }, { withCredentials: true });
      setEsito(r.data.esito); setScore(r.data.score);
      update({ idd_submitted: true, idd_esito: r.data.esito, idd_score: r.data.score });
      toast.success(`Profilatura registrata · esito: ${r.data.esito}`);
    } catch (e) {
      toast.error("Errore registrazione profilatura — il tuo intermediario sarà notificato");
      update({ idd_submitted: true });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6" data-testid="step-idd-vita">
      <div className="flex items-start gap-3 p-4 rounded-2xl bg-emerald-50 border border-emerald-200">
        <Shield className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
        <div className="text-xs text-emerald-900">
          <strong>Profilatura adeguatezza IDD</strong> — Ai sensi dell'art. 30 IDD (Reg. UE 2016/97) e dell'art. 58 Reg. IVASS 41/2018, raccogliamo informazioni per valutare l'adeguatezza del prodotto vita alle tue esigenze.
        </div>
      </div>

      <Field label="Obiettivi assicurativi/finanziari (multi-selezione)">
        <div className="space-y-2" data-testid="idd-obiettivi">
          {OBIETTIVI_OPZIONI.map(o => (
            <label key={o.id} className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={(v.obiettivi || []).includes(o.id)} onChange={() => toggleObiettivo(o.id)} className="mt-0.5" />
              <span className="text-sm text-zinc-700">{o.label}</span>
            </label>
          ))}
        </div>
      </Field>

      <Field label="Orizzonte temporale dell'investimento">
        <RadioGroup name="orizzonte" value={v.orizzonte} onChange={x => updateIdd({ orizzonte: x })} data-testid="idd-orizzonte"
          options={[{id:"<5",label:"Meno di 5 anni"},{id:"5-10",label:"5-10 anni"},{id:"10-20",label:"10-20 anni"},{id:">20",label:"Oltre 20 anni"}]} />
      </Field>

      <Field label="Propensione al rischio" tooltip="Indica quanto saresti disposto ad accettare oscillazioni del capitale per puntare a rendimenti più alti.">
        <RadioGroup name="rischio" value={v.propensione_rischio} onChange={x => updateIdd({ propensione_rischio: x })} data-testid="idd-rischio"
          options={[{id:"bassa",label:"Bassa"},{id:"media",label:"Media"},{id:"alta",label:"Alta"}]} />
      </Field>

      <Field label="Perdita massima tollerabile (% capitale)">
        <RadioGroup name="perdita" value={v.perdita_max} onChange={x => updateIdd({ perdita_max: x })} data-testid="idd-perdita"
          options={[{id:"0",label:"0%"},{id:"5",label:"Fino a 5%"},{id:"10-20",label:"10-20%"},{id:">20",label:"Oltre 20%"}]} />
      </Field>

      <Field label="Esperienza prodotti finanziari">
        <RadioGroup name="esp" value={v.esperienza_finanziaria} onChange={x => updateIdd({ esperienza_finanziaria: x })} data-testid="idd-esperienza"
          options={[{id:"nessuna",label:"Nessuna"},{id:"base",label:"Base (deposito)"},{id:"intermedia",label:"Intermedia (fondi)"},{id:"avanzata",label:"Avanzata (azioni)"}]} />
      </Field>

      <Field label="Situazione reddituale">
        <RadioGroup name="red" value={v.situazione_reddituale} onChange={x => updateIdd({ situazione_reddituale: x })} data-testid="idd-reddito"
          options={[{id:"stabile",label:"Stabile"},{id:"variabile",label:"Variabile"},{id:"a_rischio",label:"A rischio"}]} />
      </Field>

      <Field label="Composizione nucleo familiare">
        <RadioGroup name="fam" value={v.nucleo_familiare} onChange={x => updateIdd({ nucleo_familiare: x })} data-testid="idd-nucleo"
          options={[{id:"single",label:"Single"},{id:"coppia",label:"Coppia"},{id:"coppia_figli",label:"Coppia+figli"},{id:"altro",label:"Altro"}]} />
      </Field>

      <Field label="Patrimonio finanziario approssimativo">
        <RadioGroup name="patr" value={v.patrimonio_finanziario} onChange={x => updateIdd({ patrimonio_finanziario: x })} data-testid="idd-patrimonio"
          options={[{id:"<10k",label:"<10k €"},{id:"10-50k",label:"10-50k €"},{id:"50-200k",label:"50-200k €"},{id:">200k",label:">200k €"}]} />
      </Field>

      <Field label="Capacità di risparmio mensile">
        <RadioGroup name="risp" value={v.capacita_risparmio} onChange={x => updateIdd({ capacita_risparmio: x })} data-testid="idd-risparmio"
          options={[{id:"<100",label:"<100 €"},{id:"100-500",label:"100-500 €"},{id:"500-1500",label:"500-1500 €"},{id:">1500",label:">1500 €"}]} />
      </Field>

      <button type="button" onClick={submit} disabled={!isComplete || submitting}
        className="w-full sm:w-auto px-6 h-11 rounded-full bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="idd-submit-btn">
        {submitting ? "Invio in corso..." : "Registra profilatura IDD"}
      </button>

      {esito && (
        <div className={`flex items-start gap-3 p-4 rounded-2xl ${esito === "idoneo" ? "bg-emerald-50 border border-emerald-200" : esito === "idoneo_con_riserva" ? "bg-amber-50 border border-amber-200" : "bg-red-50 border border-red-200"}`} data-testid="idd-esito-banner">
          {esito === "idoneo" ? <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0" /> : <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0" />}
          <div className="text-xs">
            <strong>Esito profilatura: {esito.replace(/_/g, " ").toUpperCase()}</strong> (score {score}/100)
            {esito !== "idoneo" && <p className="mt-1 text-zinc-700">Il tuo intermediario valuterà la coerenza prima di proporti il prodotto. Puoi proseguire con la richiesta.</p>}
          </div>
        </div>
      )}
    </div>
  );
}


// ----------------------- AML adeguata verifica -----------------------
const DOC_TIPI = [
  { id: "carta_identita", label: "Carta d'identità" },
  { id: "patente", label: "Patente di guida" },
  { id: "passaporto", label: "Passaporto" },
];

export function AmlVerificaStep({ formData, update, richiestaId }) {
  const [rischio, setRischio] = useState(null);
  const [ddRafforzata, setDdRafforzata] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const v = formData.aml || {};

  const updateAml = (patch) => update({ aml: { ...v, ...patch } });

  const isComplete = v.origine_fondi && v.finalita && v.importo_annuo && v.documento_tipo && v.documento_numero && v.documento_scadenza && v.residenza_fiscale && v.is_pep !== undefined && v.is_familiare_pep !== undefined;

  const submit = async () => {
    if (!isComplete) { toast.error("Compila tutti i campi della verifica AML"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/compliance/aml-verifica`, { ...v, richiesta_id: richiestaId || null }, { withCredentials: true });
      setRischio(r.data.livello_rischio);
      setDdRafforzata(r.data.richiede_dd_rafforzata);
      update({ aml_submitted: true, aml_livello_rischio: r.data.livello_rischio });
      toast.success(`Verifica AML completata · livello rischio: ${r.data.livello_rischio}`);
    } catch (e) {
      toast.error("Errore registrazione verifica AML — l'intermediario sarà notificato");
      update({ aml_submitted: true });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6" data-testid="step-aml-vita">
      <div className="flex items-start gap-3 p-4 rounded-2xl bg-blue-50 border border-blue-200">
        <Shield className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
        <div className="text-xs text-blue-900">
          <strong>Adeguata verifica clientela (AML)</strong> — Reg. IVASS 44/2019 + D.lgs. 231/2007. Le informazioni sono trattate riservatamente e usate solo per gli obblighi antiriciclaggio.
        </div>
      </div>

      <Field label="Sei una Persona Politicamente Esposta (PEP)?" tooltip="Soggetto che ricopre o ha ricoperto cariche pubbliche di rilievo (art. 1 c.2 lett. dd) D.lgs 231/2007).">
        <RadioGroup name="pep" value={v.is_pep === true ? "si" : v.is_pep === false ? "no" : null}
          onChange={x => updateAml({ is_pep: x === "si" })} data-testid="aml-pep"
          options={[{id:"si",label:"Sì"},{id:"no",label:"No"}]} />
      </Field>

      <Field label="Hai familiari/conviventi PEP?">
        <RadioGroup name="fpep" value={v.is_familiare_pep === true ? "si" : v.is_familiare_pep === false ? "no" : null}
          onChange={x => updateAml({ is_familiare_pep: x === "si" })} data-testid="aml-familiare-pep"
          options={[{id:"si",label:"Sì"},{id:"no",label:"No"}]} />
      </Field>

      <Field label="Origine dei fondi">
        <RadioGroup name="origine" value={v.origine_fondi} onChange={x => updateAml({ origine_fondi: x })} data-testid="aml-origine"
          options={[{id:"lavoro_dipendente",label:"Lavoro dipendente"},{id:"lavoro_autonomo",label:"Lavoro autonomo"},{id:"pensione",label:"Pensione"},{id:"eredita",label:"Eredità"},{id:"investimenti",label:"Investimenti"},{id:"vendita_immobile",label:"Vendita immobile"},{id:"altro",label:"Altro"}]} />
      </Field>

      <Field label="Finalità dell'operazione">
        <RadioGroup name="fin" value={v.finalita} onChange={x => updateAml({ finalita: x })} data-testid="aml-finalita"
          options={[{id:"protezione_famiglia",label:"Protezione famiglia"},{id:"risparmio",label:"Risparmio"},{id:"investimento",label:"Investimento"},{id:"altro",label:"Altro"}]} />
      </Field>

      <Field label="Importo annuo previsto" tooltip="Importo cumulativo annuale; soglia 15.000 € attiva controlli rafforzati ex D.lgs. 231/2007.">
        <RadioGroup name="imp" value={v.importo_annuo} onChange={x => updateAml({ importo_annuo: x })} data-testid="aml-importo"
          options={[{id:"<5k",label:"<5.000 €"},{id:"5-15k",label:"5.000-15.000 €"},{id:"15-50k",label:"15.000-50.000 €"},{id:">50k",label:">50.000 €"}]} />
      </Field>

      <Field label="Documento di identità">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <select value={v.documento_tipo || ""} onChange={e => updateAml({ documento_tipo: e.target.value })} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm" data-testid="aml-doc-tipo">
            <option value="">Seleziona...</option>
            {DOC_TIPI.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
          <input type="text" placeholder="Numero" value={v.documento_numero || ""} onChange={e => updateAml({ documento_numero: e.target.value.toUpperCase() })}
            className="px-3 py-2 rounded-lg border border-zinc-200 text-sm" data-testid="aml-doc-numero" />
          <input type="date" placeholder="Scadenza" value={v.documento_scadenza || ""} onChange={e => updateAml({ documento_scadenza: e.target.value })}
            className="px-3 py-2 rounded-lg border border-zinc-200 text-sm" data-testid="aml-doc-scadenza" />
        </div>
      </Field>

      <Field label="Residenza fiscale">
        <select value={v.residenza_fiscale || ""} onChange={e => updateAml({ residenza_fiscale: e.target.value })}
          className="w-full sm:w-64 px-3 py-2 rounded-lg border border-zinc-200 text-sm" data-testid="aml-residenza">
          <option value="">Seleziona...</option>
          <option value="IT">🇮🇹 Italia</option>
          <option value="FR">🇫🇷 Francia</option>
          <option value="DE">🇩🇪 Germania</option>
          <option value="ES">🇪🇸 Spagna</option>
          <option value="CH">🇨🇭 Svizzera</option>
          <option value="UK">🇬🇧 Regno Unito</option>
          <option value="USA">🇺🇸 Stati Uniti</option>
          <option value="ALTRO">🌐 Altro paese</option>
        </select>
      </Field>

      <button type="button" onClick={submit} disabled={!isComplete || submitting}
        className="w-full sm:w-auto px-6 h-11 rounded-full bg-blue-700 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="aml-submit-btn">
        {submitting ? "Invio..." : "Registra verifica AML"}
      </button>

      {rischio && (
        <div className={`flex items-start gap-3 p-4 rounded-2xl ${rischio === "LOW" ? "bg-emerald-50 border border-emerald-200" : rischio === "MEDIUM" ? "bg-amber-50 border border-amber-200" : "bg-red-50 border border-red-200"}`} data-testid="aml-rischio-banner">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <div className="text-xs">
            <strong>Livello rischio: {rischio}</strong>
            {ddRafforzata && <p className="mt-1 text-zinc-700">Il tuo profilo richiede <strong>due diligence rafforzata</strong>. L'intermediario contatterà a breve per ulteriori dettagli.</p>}
          </div>
        </div>
      )}
    </div>
  );
}


// ----------------------- helpers UI -----------------------
function Field({ label, tooltip, children }) {
  const [tipOpen, setTipOpen] = useState(false);
  return (
    <div>
      <div className="flex items-center gap-1 mb-2">
        <label className="text-sm font-semibold text-zinc-900">{label}</label>
        {tooltip && (
          <button type="button" onMouseEnter={() => setTipOpen(true)} onMouseLeave={() => setTipOpen(false)}
            onClick={() => setTipOpen(v => !v)} className="relative inline-flex items-center" aria-describedby={`tip-${label}`}>
            <Info className="w-3.5 h-3.5 text-zinc-400" />
            {tipOpen && (
              <span id={`tip-${label}`} role="tooltip"
                className="absolute z-50 top-5 left-0 min-w-[240px] bg-zinc-900 text-white text-[11px] leading-snug rounded-lg px-3 py-2 shadow-xl text-left">
                {tooltip}
              </span>
            )}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}
